import React, { useEffect, useState } from 'react';
import { Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import { getEventQuizzesApi, createEventQuizApi, deleteEventQuizApi, getCommunityQuizApi, submitCommunityQuizAnswerApi } from '../../../api/events';
import { Modal } from 'react-bootstrap';

export const EventQuizzes = ({ eventId, communityId, isOwner }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', questions: [] });
  const [submitting, setSubmitting] = useState(false);
  const [showTakeQuizModal, setShowTakeQuizModal] = useState(false);
  const [activeQuizId, setActiveQuizId] = useState(null);

  useEffect(() => {
    if (eventId) fetchQuizzes();
  }, [eventId]);

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      const res = await getEventQuizzesApi({ eventId });
      setQuizzes(res.data || []);
    } catch (err) {
      console.warn('Failed to fetch quizzes', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;
    try {
      await deleteEventQuizApi(id);
      fetchQuizzes();
    } catch (err) {
      alert('Failed to delete quiz.');
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      alert('Please provide a title');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        communityId,
        eventId,
        title: formData.title,
        description: formData.description,
        mcqs: formData.questions
      };
      await createEventQuizApi(payload);
      setShowAddForm(false);
      setFormData({ title: '', description: '', questions: [] });
      fetchQuizzes();
    } catch (err) {
      alert('Failed to add quiz.');
    } finally {
      setSubmitting(false);
    }
  };

  if (showAddForm) {
    return (
      <div className="bg-light p-3 rounded-4 border">
        <div className="d-flex align-items-center mb-4">
          <button className="btn btn-link text-dark p-0 me-3" onClick={() => setShowAddForm(false)}>
            <i className="bi bi-chevron-left fs-5"></i>
          </button>
          <h5 className="fw-bold mb-0">Add Quiz Questions</h5>
        </div>
        
        <Form onSubmit={handleAddSubmit}>
          <Form.Group className="mb-4">
            <Form.Label className="text-muted small fw-semibold">Quiz title</Form.Label>
            <Form.Control 
              type="text" 
              className="py-2 rounded-3"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              required 
            />
          </Form.Group>
          
          <Form.Group className="mb-4">
            <Form.Label className="text-muted small fw-semibold">Quiz Description</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={4}
              className="py-2 rounded-3"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
            />
          </Form.Group>
          
          <div className="mb-4">
            <h6 className="fw-bold mb-1 text-muted small">Quiz Questions</h6>
            <p className="text-muted small mb-3">Create quiz questions to check how well community users understand the lesson.</p>
            
            {formData.questions.map((q, qIdx) => (
              <div key={qIdx} className="bg-white p-3 rounded-3 border mb-3 position-relative">
                <Button 
                  variant="link" 
                  className="text-danger p-0 position-absolute top-0 end-0 m-2 text-decoration-none shadow-none" 
                  onClick={() => {
                    const newQ = [...formData.questions];
                    newQ.splice(qIdx, 1);
                    setFormData({ ...formData, questions: newQ });
                  }}
                >
                  <i className="bi bi-trash"></i>
                </Button>
                
                <Form.Group className="mb-3 mt-2">
                  <Form.Label className="small fw-semibold">Question {qIdx + 1}</Form.Label>
                  <Form.Control 
                    as="textarea"
                    rows={2}
                    placeholder="Enter question text..."
                    value={q.question || ''}
                    onChange={(e) => {
                      const newQ = [...formData.questions];
                      newQ[qIdx].question = e.target.value;
                      setFormData({ ...formData, questions: newQ });
                    }}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small fw-semibold">Quiz Type</Form.Label>
                  <Form.Select 
                    value={q.quizType}
                    onChange={(e) => {
                      const newQ = [...formData.questions];
                      newQ[qIdx].quizType = e.target.value;
                      setFormData({ ...formData, questions: newQ });
                    }}
                  >
                    <option value="singleChoice">Single Choice</option>
                    <option value="multipleChoice">Multiple Choice</option>
                    <option value="paragraph">Paragraph</option>
                  </Form.Select>
                </Form.Group>

                {q.quizType === 'paragraph' ? (
                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold">Correct Answer</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={2} 
                      placeholder="Enter the expected correct answer"
                      value={q.answer || ''}
                      onChange={(e) => {
                        const newQ = [...formData.questions];
                        newQ[qIdx].answer = e.target.value;
                        setFormData({ ...formData, questions: newQ });
                      }}
                    />
                  </Form.Group>
                ) : (
                  <div className="mb-2">
                    <Form.Label className="small fw-semibold">Options</Form.Label>
                    {(q.options || []).map((opt, optIdx) => (
                      <div key={optIdx} className="d-flex align-items-center mb-2 gap-2">
                        <Form.Check 
                          type={q.quizType === 'singleChoice' ? 'radio' : 'checkbox'}
                          name={`mcq_${qIdx}_option_radio`}
                          checked={opt.isCorrect || false}
                          onChange={(e) => {
                            const newQ = [...formData.questions];
                            if (q.quizType === 'singleChoice') {
                              newQ[qIdx].options.forEach(o => o.isCorrect = false);
                            }
                            newQ[qIdx].options[optIdx].isCorrect = e.target.checked;
                            setFormData({ ...formData, questions: newQ });
                          }}
                        />
                        <Form.Control 
                          type="text" 
                          placeholder={`Option ${optIdx + 1}`}
                          required
                          value={opt.text || ''}
                          onChange={(e) => {
                            const newQ = [...formData.questions];
                            newQ[qIdx].options[optIdx].text = e.target.value;
                            setFormData({ ...formData, questions: newQ });
                          }}
                        />
                        {q.options.length > 2 && (
                          <Button variant="outline-danger" size="sm" onClick={() => {
                            const newQ = [...formData.questions];
                            newQ[qIdx].options.splice(optIdx, 1);
                            setFormData({ ...formData, questions: newQ });
                          }}>
                            <i className="bi bi-x"></i>
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => {
                      const newQ = [...formData.questions];
                      newQ[qIdx].options.push({ text: '', isCorrect: false });
                      setFormData({ ...formData, questions: newQ });
                    }}>
                      <i className="bi bi-plus"></i> Add Option
                    </Button>
                  </div>
                )}
              </div>
            ))}
            
            <Button 
              variant="outline-primary" 
              className="w-100 rounded-3 py-2 text-primary border-primary bg-white"
              onClick={() => {
                setFormData({ 
                  ...formData, 
                  questions: [
                    ...formData.questions, 
                    { question: '', quizType: 'singleChoice', options: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }], answer: '' }
                  ] 
                });
              }}
            >
              <i className="bi bi-plus"></i> Add Question
            </Button>
          </div>
          
          <div className="mt-5 pt-3">
            <Button variant="primary" type="submit" disabled={submitting || formData.questions.length === 0} className="w-100 rounded-3 py-3 fw-bold border-0" style={{ backgroundColor: '#2124b1' }}>
              {submitting ? 'Adding...' : 'Save Quiz'}
            </Button>
          </div>
        </Form>
      </div>
    );
  }

  return (
    <div>
      {isOwner && (
        <div className="d-flex justify-content-end mb-3">
          <Button variant="primary" className="rounded-pill px-4 cd-btn-orange border-0" onClick={() => setShowAddForm(true)}>
            <i className="bi bi-plus-lg me-1"></i> Create Quiz
          </Button>
        </div>
      )}
      
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : quizzes.length === 0 ? (
        <div className="text-center py-5">
          <i className="bi bi-ui-checks text-muted" style={{ fontSize: '3rem' }}></i>
          <h5 className="mt-3">No Quizzes Yet</h5>
          <p className="text-muted">No quizzes have been created.</p>
        </div>
      ) : (
        <Row className="g-3">
          {quizzes.map((quiz) => (
            <Col md={6} key={quiz._id}>
              <div className="card h-100 border-0 shadow-sm rounded-4 position-relative">
                {isOwner && (
                  <button 
                    className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2 rounded-circle shadow-sm"
                    onClick={() => handleDelete(quiz._id)}
                    title="Delete Quiz"
                    style={{ zIndex: 2 }}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                )}
                <div className="card-body text-center p-4">
                  <i className="bi bi-check2-square text-primary" style={{ fontSize: '2.5rem' }}></i>
                  <h6 className="mt-3 mb-1 fw-bold text-truncate">{quiz.title}</h6>
                  <p className="text-muted small text-truncate-2 mb-2">{quiz.description}</p>
                  {quiz.createdAt && <small className="text-muted d-block mb-3">{new Date(quiz.createdAt).toLocaleDateString()}</small>}
                  <Button variant="outline-primary" size="sm" className="rounded-pill px-3 mt-auto" onClick={() => { setActiveQuizId(quiz._id); setShowTakeQuizModal(true); }}>
                    Take Quiz
                  </Button>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}
      
      <TakeQuizModal 
        show={showTakeQuizModal} 
        onHide={() => setShowTakeQuizModal(false)} 
        quizId={activeQuizId} 
      />
    </div>
  );
};

export const TakeQuizModal = ({ show, onHide, quizId }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (show && quizId) {
      loadQuiz();
    }
  }, [show, quizId]);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      const res = await getCommunityQuizApi(quizId);
      const quizInfo = res.data || {};
      setQuizData(quizInfo);
      
      const initAnswers = {};
      if (quizInfo.questions) {
        quizInfo.questions.forEach(q => {
          if (q.userAnswer) {
             initAnswers[q._id] = {
               questionId: q._id,
               selectedOptionId: q.userAnswer.selectedOptionId,
               selectedOptionIds: q.userAnswer.selectedOptionIds || [],
               paragraphAnswer: q.userAnswer.paragraphAnswer || ''
             };
          }
        });
      }
      setAnswers(initAnswers);
    } catch (err) {
      console.error(err);
      alert('Failed to load quiz');
      onHide();
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (questionId, optionId, type) => {
    setAnswers(prev => {
      const current = prev[questionId] || {};
      if (type === 'singleChoice') {
        return { ...prev, [questionId]: { ...current, questionId, selectedOptionId: optionId } };
      } else if (type === 'multipleChoice') {
        const selectedIds = current.selectedOptionIds || [];
        const isSelected = selectedIds.includes(optionId);
        const newIds = isSelected ? selectedIds.filter(id => id !== optionId) : [...selectedIds, optionId];
        return { ...prev, [questionId]: { ...current, questionId, selectedOptionIds: newIds } };
      }
      return prev;
    });
  };

  const handleTextChange = (questionId, text) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { ...(prev[questionId] || {}), questionId, paragraphAnswer: text }
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const formattedAnswers = Object.values(answers).map(ans => ({
        questionId: ans.questionId,
        selectedOptionId: ans.selectedOptionId || null,
        selectedOptionIds: ans.selectedOptionIds || [],
        paragraphAnswer: ans.paragraphAnswer || ''
      }));

      await submitCommunityQuizAnswerApi({
        community_quiz_id: quizId,
        answers: formattedAnswers
      });
      alert('Quiz submitted successfully!');
      onHide();
    } catch (err) {
      console.error(err);
      alert('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title>{quizData?.title || 'Take Quiz'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
        ) : (
          <div>
            <p className="text-muted mb-4">{quizData?.description}</p>
            {quizData?.questions?.map((q, idx) => (
              <div key={q._id} className="mb-4 bg-light p-3 rounded-3">
                <h6 className="fw-bold mb-3">{idx + 1}. {q.question}</h6>
                {q.quizType === 'singleChoice' && q.options && q.options.map(opt => (
                  <Form.Check 
                    key={opt._id}
                    type="radio"
                    id={`opt-${opt._id}`}
                    label={
                      <span className="d-flex align-items-center">
                        {opt.text}
                        {quizData?.isAnswered && opt.isCorrect && (
                          <span className="badge bg-success ms-2"><i className="bi bi-check-circle-fill me-1"></i>Correct</span>
                        )}
                        {quizData?.isAnswered && answers[q._id]?.selectedOptionId === opt._id && !opt.isCorrect && (
                          <span className="badge bg-danger ms-2"><i className="bi bi-x-circle-fill me-1"></i>Incorrect</span>
                        )}
                      </span>
                    }
                    name={`q-${q._id}`}
                    checked={answers[q._id]?.selectedOptionId === opt._id}
                    onChange={() => handleOptionSelect(q._id, opt._id, 'singleChoice')}
                    disabled={quizData?.isAnswered}
                    className="mb-2"
                  />
                ))}
                {q.quizType === 'multipleChoice' && q.options && q.options.map(opt => (
                  <Form.Check 
                    key={opt._id}
                    type="checkbox"
                    id={`opt-${opt._id}`}
                    label={
                      <span className="d-flex align-items-center">
                        {opt.text}
                        {quizData?.isAnswered && opt.isCorrect && (
                          <span className="badge bg-success ms-2"><i className="bi bi-check-circle-fill me-1"></i>Correct</span>
                        )}
                        {quizData?.isAnswered && answers[q._id]?.selectedOptionIds?.includes(opt._id) && !opt.isCorrect && (
                          <span className="badge bg-danger ms-2"><i className="bi bi-x-circle-fill me-1"></i>Incorrect</span>
                        )}
                      </span>
                    }
                    checked={answers[q._id]?.selectedOptionIds?.includes(opt._id)}
                    onChange={() => handleOptionSelect(q._id, opt._id, 'multipleChoice')}
                    disabled={quizData?.isAnswered}
                    className="mb-2"
                  />
                ))}
                {q.quizType === 'paragraph' && (
                  <Form.Control 
                    as="textarea"
                    rows={3}
                    value={answers[q._id]?.paragraphAnswer || ''}
                    onChange={(e) => handleTextChange(q._id, e.target.value)}
                    disabled={quizData?.isAnswered}
                    placeholder="Enter your answer..."
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="secondary" onClick={onHide} className="rounded-pill px-4">Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading || submitting || quizData?.isAnswered} className="rounded-pill px-4 cd-btn-orange border-0">
          {quizData?.isAnswered ? 'Already Submitted' : submitting ? 'Submitting...' : 'Submit Answers'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

